import classnames from 'classnames';
import React, { useRef, useState, useEffect, useContext } from 'react';
import styled, { ThemeContext, ThemeProvider } from 'styled-components';
import { useClickOutside } from '../../../../hooks/use-click-outside.js';
import { convertHex3toHex6, isValidColor } from '../../../../utilities/color.js';
import 'date-fns';
import '../../../../utilities/locale.js';
import '../../../../hooks/use-prevent-keyboard-scrolling.js';
import '../../../../themes/alert.js';
import '../../../../themes/button.js';
import '../../../../themes/checkbox.js';
import '../../../../themes/divider.js';
import '../../../../themes/dropdown.js';
import '../../../../themes/fieldset.js';
import '../../../../themes/input.js';
import '../../../../themes/list.js';
import '../../../../themes/listbox.js';
import '../../../../themes/loader.js';
import '../../../../themes/option.js';
import '../../../../themes/popout.js';
import '../../../../themes/switch.js';
import { lightTheme } from '../../../../themes/themes.js';
import '../../../../themes/tooltip.js';
import '../../../atoms/icon/font-icon/font-icon.js';
import 'react-transition-group';
import '../../../atoms/alert/inline-alert/inline-alert.js';
import '../../../atoms/alert/standard-alert/standard-alert.js';
import 'boring-avatars';
import '../../../atoms/tooltip/hover-tooltip/hover-tooltip.js';
import 'react-laag';
import '../../../../styles/styled-components/alert.js';
import '../../../../styles/styled-components/animations.js';
import '../../../../styles/styled-components/box-shadow.js';
import '../../../../styles/styled-components/button.js';
import '../../../../styles/styled-components/font-face.js';
import '../../../../styles/styled-components/global.js';
import '../../../../styles/styled-components/input.js';
import '../../../../styles/styled-components/list.js';
import '../../../../styles/styled-components/listitem.js';
import '../../../../styles/styled-components/media-queries.js';
import '../../../../styles/styled-components/reset.js';
import '../../../../styles/styled-components/outline.js';
import '../../../../styles/styled-components/popout.js';
import '../../../../styles/styled-components/screen-reader.js';
import '../../../../styles/styled-components/switch.js';
import '../../../../styles/styled-components/tooltip.js';
import '@brandfolder/utilities';
import '../../../atoms/button/close-button/close-button.js';
import { ColorButton } from '../../../atoms/button/color-button/color-button.js';
import '../../../atoms/button/icon-button/icon-button.js';
import '../../../atoms/button/standard-button/standard-button.js';
import '../../../atoms/loader/circle-loader/circle-loader.js';
import '../../../atoms/loader/shimmer-loader/shimmer-loader.js';
import '../../../atoms/divider/standard-divider/standard-divider.js';
import '../../../atoms/fieldset/standard-fieldset/standard-fieldset.js';
import '../../../atoms/heading/standard-heading/standard-heading.js';
import '../../../atoms/label/standard-label/standard-label.js';
import '../../../atoms/link/button-link/button-link.js';
import '../../../atoms/link/standard-link/standard-link.js';
import { StandardPopout } from '../../../atoms/popout/standard-popout/standard-popout.js';
import '../../../atoms/screen-reader/only-for-screen-reader/only-for-screen-reader.js';
import '../../../atoms/table/standard-table/caption.js';
import '../../../atoms/table/standard-table/tbody.js';
import '../../../atoms/table/standard-table/tr-empty.js';
import '../../../atoms/table/standard-table/td.js';
import '../../../atoms/table/standard-table/tr.js';
import '../../../atoms/table/standard-table/table.js';
import '../../../atoms/table/standard-table/tfoot.js';
import '../../../atoms/table/standard-table/th.js';
import '../../../atoms/table/standard-table/thead.js';
import '../../../atoms/table/dnd-table/dnd-handle-td.js';
import '../../../atoms/table/dnd-table/dnd-table-drag-context.js';
import '../../../atoms/text/help-text/help-text.js';
import '../../../atoms/text/standard-text/standard-text.js';
import { StandardTextfield } from '../../textfield/standard-textfield/standard-textfield.js';
import { StandardColorPicker } from '../standard-color-picker/standard-color-picker.js';

const BaseHexColorPicker = (props)=>{
    const { buttonProps, className, colorPickerProps, defaultColor, id, insidePopout, labels, onChange, popoutProps, textfieldProps, ...otherProps } = props;
    const { buttonLabel, closeButtonLabel, loaderLabel, requiredLabel, textfieldLabel } = labels;
    const DEFAULT_COLOR = '#ffffff';
    const ref = useRef(null);
    const { clickedOutside } = useClickOutside({
        ref
    });
    const [color, setColor] = useState(defaultColor || DEFAULT_COLOR);
    const [hex, setHex] = useState(defaultColor || DEFAULT_COLOR);
    const [mounted, setMounted] = useState(false);
    const [open, setOpen] = useState(false);
    const handleOnClose = ()=>{
        if (isValidColor(hex)) {
            setColor(hex);
        }
        setOpen(false);
    };
    useEffect(()=>{
        setMounted(true);
    }, []);
    useEffect(()=>{
        if (clickedOutside && insidePopout && mounted) {
            handleOnClose();
        }
    }, [
        clickedOutside
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(()=>{
        if (mounted) {
            setHex(color);
            onChange(color);
        }
    }, [
        color
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    const colorPickerId = `${id}-color-picker`;
    const popoutId = `${id}-popout`;
    const textfieldId = `${id}-textfield`;
    return /*#__PURE__*/ React.createElement("div", {
        ...otherProps,
        className: classnames({
            'bf-textfield__hex': true,
            [`${className}`]: !!className
        }),
        id: id,
        ref: ref
    }, /*#__PURE__*/ React.createElement(StandardTextfield, {
        ...textfieldProps,
        id: textfieldId,
        label: textfieldLabel,
        onBlur: (e)=>{
            const { value } = e.target;
            const newHex = convertHex3toHex6(value);
            if (newHex !== color && isValidColor(newHex)) {
                setColor(newHex);
            } else {
                setHex(color);
            }
            if (textfieldProps?.onBlur) {
                textfieldProps.onBlur(e);
            }
        },
        onChange: (e)=>{
            setHex(e.target.value);
            if (textfieldProps?.onChange) {
                textfieldProps.onChange(e);
            }
        },
        requiredLabel: requiredLabel,
        value: hex
    }), /*#__PURE__*/ React.createElement(StandardPopout, {
        ...popoutProps,
        closeOnClickAway: true,
        content: /*#__PURE__*/ React.createElement(StandardColorPicker, {
            ...colorPickerProps,
            defaultColor: hex,
            id: colorPickerId,
            onChange: (colors)=>{
                setHex(colors.hex);
            },
            showHsl: false,
            showRgb: false
        }),
        id: popoutId,
        labels: {
            closeButtonLabel,
            loaderLabel
        },
        open: open,
        onClose: ()=>{
            handleOnClose();
        },
        setOpen: setOpen,
        width: 250
    }, /*#__PURE__*/ React.createElement(ColorButton, {
        ...buttonProps,
        "aria-controls": popoutId,
        "aria-expanded": open,
        "aria-haspopup": "dialog",
        "aria-label": buttonLabel,
        backgroundColor: isValidColor(hex) ? hex : DEFAULT_COLOR,
        onClick: (e)=>{
            setOpen(!open);
            if (buttonProps?.onClick) {
                buttonProps.onClick(e);
            }
        },
        onKeyUp: (e)=>{
            if (e.key === 'Enter') {
                setOpen(!open);
            }
            if (buttonProps?.onKeyUp) {
                buttonProps.onKeyUp(e);
            }
        }
    })));
};
const TextfieldColorPicker = styled(BaseHexColorPicker)`
  position: relative;

  .bf-color-picker__standard {
    width: 100%;
  }

  .bf-popout__standard {
    bottom: 7px;
    position: absolute;
    right: 10px;

    @-moz-document url-prefix() {
      bottom: 13px;
    }
  }

  .bf-popout__header {
    border-bottom: 0;
    display: block;
    padding: 0;
  }

  .bf-popout__body {
    padding: 30px 35px 30px 30px;
  }
`;
const HexColorPicker = (props)=>{
    const themeContext = useContext(ThemeContext);
    return /*#__PURE__*/ React.createElement(ThemeProvider, {
        theme: themeContext || lightTheme
    }, /*#__PURE__*/ React.createElement(TextfieldColorPicker, props));
};

export { HexColorPicker };
//# sourceMappingURL=hex-color-picker.js.map
